<div class="member-selection-section">

    <!-- Search and Sort Controls -->
    <div class="d-flex justify-content-between flex-wrap gap-3 mb-3">
        <div class="d-flex flex-column flex-md-row gap-3 flex-grow-1">
            <!-- Search Input -->
            <div class="input-group position-relative member-search-input">
                <input id="memberSearch" type="text" class="form-control form-control-solid ps-13 pe-5"
                    placeholder="Search by name or email" aria-label="Search members"
                    [ngModel]="searchTerm"
                    (ngModelChange)="searchTerm = $event"
                    (keyup.enter)="onSearch()" aria-describedby="memberSearch">

                @if (searchTerm) {
                    <button
                        class="btn btn-link position-absolute top-50 translate-middle-y p-0 border-0 bg-transparent mx-2 input-group-clear-button"
                        type="button"
                        (click)="clearSearch()"
                        aria-label="Clear search">
                        <fa-duotone-icon icon="xmark" class="text-secondary fs-6" secondaryOpacity="0.9" size="lg" />
                    </button>
                }
                <div class="input-group-append">
                    <button class="btn btn-primary rounded-0 rounded-end"
                            type="button"
                            (click)="onSearch()"
                            id="member-button-addon">
                        Search
                    </button>
                </div>
            </div>

            <div class="d-flex gap-3">
                <ms-member-search-filter
                    [localFilter]="membersFilter"
                    [defaultFilterFactory]="membersFilterDefaultFactory"
                    (filterApplied)="onFilterApplied($event)">
                </ms-member-search-filter>

                <!-- Sort Dropdown -->
                <ms-sort-dropdown
                    [sortOptions]="sortOptions"
                    [(selectedSortOption)]="orderBy"
                    (selectedSortOptionChange)="onSortChange($event)">
                </ms-sort-dropdown>
            </div>
        </div>
    </div>

    <!-- Member Count and Select All -->
    <div class="d-flex justify-content-between align-items-center mb-2">
        <div #jumpToElementMobile>
            <span class="me-2">Showing: {{ displayMembers.length }} of {{ totalCount | number }}</span>
            <span class="badge bg-primary">Selected: {{ effectiveSelectedCount | number }}</span>
        </div>
        <button
            type="button"
            class="btn btn-sm btn-ms-secondary d-md-none"
            (click)="toggleSelectAll()">
            {{ isSelectAll ? 'Deselect All' : 'Select All' }}
        </button>
    </div>

    <!-- Member Table (Desktop) — .table-responsive provides its own scroll/height -->
    <div class="table-responsive d-none d-md-block">
        <table msResponsiveTable class="table table-hover placeholder-glow">
            <thead #jumpToElement>
                <tr>
                    <th msResponsiveTableColumn="checkbox" [priority]="1" scope="col">
                        <input
                            type="checkbox"
                            class="form-check-input fs-6"
                            [checked]="isSelectAll"
                            (change)="toggleSelectAll()" />
                    </th>
                    <th msResponsiveTableColumn="name" [priority]="1" scope="col">Name</th>
                    <th msResponsiveTableColumn="email" [priority]="2" scope="col">Email</th>
                    <th msResponsiveTableColumn="phone" [priority]="3" scope="col">Phone</th>
                </tr>
            </thead>
            <tbody>
                @for (member of displayMembers; let i = $index; track member.memberId) {
                    <tr class="align-middle" [className]="(i % 2 === 0) ? 'even' : 'odd'">
                        <td msResponsiveTableColumn="checkbox" [priority]="1">
                            <input
                                type="checkbox"
                                class="form-check-input"
                                [checked]="isSelected(member.memberId)"
                                (change)="onMemberToggle(member.memberId, $event)" />
                        </td>
                        <td msResponsiveTableColumn="name" [priority]="1">
                            <span class="cell-truncate" [class.placeholder]="isLoadingMembers"
                                [msTooltip]="member.firstName + ' ' + member.lastName" msTooltipOnlyIfTruncated>{{ member.firstName }} {{ member.lastName }}</span>
                        </td>
                        <td msResponsiveTableColumn="email" [priority]="2">
                            <span class="cell-truncate" [class.placeholder]="isLoadingMembers"
                                [msTooltip]="member.emailAddress ?? ''" msTooltipOnlyIfTruncated>{{ member.emailAddress | noValue }}</span>
                        </td>
                        <td msResponsiveTableColumn="phone" [priority]="3">
                            <span class="cell-truncate" [class.placeholder]="isLoadingMembers">{{ member.phoneNumber | NANPFormattedPhone | noValue }}</span>
                        </td>
                    </tr>
                }
            </tbody>
            <tfoot ms-pagination [take]="take" (loadMoreInView)="loadMoreInView()" [(skip)]="skip"
                [hasNext]="hasNext" [itemCount]="displayMembers.length" (jumpToClicked)="jumpToTop($event)">
            </tfoot>
        </table>
    </div>

    <!-- Member Cards (Mobile) — height-capped so the compose form stays reachable -->
    <div class="d-block d-md-none recipient-list-scroll">
        @for (member of displayMembers; track member.memberId) {
            <div class="card mb-3">
                <div class="card-body">
                    <div class="d-flex align-items-start">
                        <div class="d-flex align-items-center me-3">
                            <ms-mobile-card-checkbox
                                [item]="member" [selectionPrimaryKey]="SelectionPrimaryKeys.MemberId"
                                [isSelected]="isSelected(member.memberId)"
                                (clicked)="onToggle($event)" />
                        </div>
                        <div class="flex-grow-1 min-w-0">
                            <h6 class="card-title mb-2 text-truncate" [class.placeholder]="isLoadingMembers"
                                [msTooltip]="member.firstName + ' ' + member.lastName" msTooltipOnlyIfTruncated>{{ member.firstName }} {{ member.lastName }}</h6>
                            <p class="card-text mb-1 text-truncate" [msTooltip]="member.emailAddress ?? ''" msTooltipOnlyIfTruncated><strong class="me-1">Email:</strong><span [class.placeholder]="isLoadingMembers">{{ member.emailAddress | noValue }}</span></p>
                            <p class="card-text mb-0 text-truncate"><strong class="me-1">Phone:</strong><span [class.placeholder]="isLoadingMembers">{{ member.phoneNumber | NANPFormattedPhone | noValue }}</span></p>
                        </div>
                    </div>
                </div>
            </div>
        }
        <div ms-in-view-port (inViewPort)="loadMoreInView()" ms-pagination [take]="take" [(skip)]="skip"
            [hasNext]="hasNext" [itemCount]="displayMembers.length" (jumpToClicked)="jumpToTop($event)">
        </div>
    </div>

    @if (!isLoadingMembers && displayMembers.length === 0) {
        <div class="text-center text-muted py-5">
            @if (loadError()) {
                {{ CommunicationsMessagingConstants.RECIPIENT_LIST_LOAD_FAILED() }}
            } @else {
                {{ CommunicationsMessagingConstants.RECIPIENT_LIST_EMPTY() }}
            }
        </div>
    }
</div>
